import { LocaleCatalogEntry, SupportedLocale } from 'src/environments/locale.model';

/**
 * Normalize a language/culture tag to BCP-47 with hyphen and canonical casing
 * (language lower, region upper): en_ZA → en-ZA, EN-us → en-US.
 */
export function normalizeCultureTag(tag: string | null | undefined): string {
  if (!tag || typeof tag !== 'string') {
    return '';
  }
  const trimmed = tag.trim().replace(/_/g, '-');
  if (!trimmed) {
    return '';
  }
  const parts = trimmed.split('-').filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  const language = parts[0].toLowerCase();
  if (parts.length === 1) {
    return language;
  }
  const rest = parts.slice(1).map((p, i) => {
    // Region (typically 2 letters) → UPPER; script (4 letters) → Title case
    if (p.length === 2 && i === 0) {
      return p.toUpperCase();
    }
    if (p.length === 4 && /^[a-zA-Z]+$/.test(p)) {
      return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
    }
    return p;
  });
  return [language, ...rest].join('-');
}

export function languageSubtag(culture: string): string {
  const normalized = normalizeCultureTag(culture);
  if (!normalized) {
    return '';
  }
  return normalized.split('-')[0];
}

/**
 * True when the culture uses US month-first dates (MM/DD/YYYY).
 * All other cultures use YYYY/MM/DD in date inputs.
 */
export function usesUSDateFormat(culture: string | null | undefined): boolean {
  return normalizeCultureTag(culture).toLowerCase().startsWith('en-us');
}

/** True when the active culture is English (United Kingdom). */
export function usesGBWeightUnits(culture: string | null | undefined): boolean {
  return normalizeCultureTag(culture).toLowerCase() === 'en-gb';
}

/**
 * Normalize catalog entries from shared-config / environment (legacy `code` or SupportedLocale).
 */
export function normalizeLocaleCatalog(entries: LocaleCatalogEntry[] | null | undefined): SupportedLocale[] {
  if (!entries?.length) {
    return [];
  }
  const result: SupportedLocale[] = [];
  for (const entry of entries) {
    if (!entry) {
      continue;
    }
    const legacy = entry as { code?: string; culture?: string; text: string; dir: 'ltr' | 'rtl' };
    const culture = normalizeCultureTag(legacy.culture || legacy.code || '');
    if (!culture) {
      continue;
    }
    result.push({
      culture,
      text: legacy.text,
      dir: legacy.dir === 'rtl' ? 'rtl' : 'ltr',
    });
  }
  return result;
}

/**
 * Effective allowlist: flavour override when present, otherwise shared catalog.
 */
export function resolveEffectiveLocales(
  environmentLocales: LocaleCatalogEntry[] | null | undefined,
  sharedLocales: LocaleCatalogEntry[],
): SupportedLocale[] {
  if (environmentLocales != null && environmentLocales.length > 0) {
    return normalizeLocaleCatalog(environmentLocales);
  }
  return normalizeLocaleCatalog(sharedLocales);
}

export function resolveDefaultCulture(
  environmentDefaultCulture: string | null | undefined,
  sharedDefaultCulture: string,
  allowlist: SupportedLocale[],
): string {
  const preferred = normalizeCultureTag(environmentDefaultCulture || sharedDefaultCulture);
  if (preferred && allowlist.some((l) => l.culture === preferred)) {
    return preferred;
  }
  if (allowlist.length > 0) {
    return allowlist[0].culture;
  }
  return preferred || 'en-ZA';
}

/**
 * Match a requested tag against the allowlist:
 * exact culture → same language any region → defaultCulture.
 */
export function matchSupportedLocale(
  requested: string | null | undefined,
  allowlist: SupportedLocale[],
  defaultCulture: string,
): SupportedLocale {
  const fallbackCulture = resolveDefaultCulture(defaultCulture, defaultCulture, allowlist);
  const fallback: SupportedLocale =
    allowlist.find((l) => l.culture === fallbackCulture) ||
    allowlist[0] || {
      culture: fallbackCulture || 'en-ZA',
      text: 'English',
      dir: 'ltr',
    };

  if (!allowlist.length) {
    return fallback;
  }

  const normalized = normalizeCultureTag(requested);
  if (!normalized) {
    return fallback;
  }

  const exact = allowlist.find((l) => l.culture === normalized);
  if (exact) {
    return exact;
  }

  const lang = languageSubtag(normalized);

  // Legacy bare language (e.g. "en") or unknown region: first culture for that language
  const sameLanguage = allowlist.find((l) => languageSubtag(l.culture) === lang);
  if (sameLanguage) {
    return sameLanguage;
  }

  return fallback;
}

/**
 * Resolve registration/profile culture from explicit → current → device candidates.
 */
export function resolvePreferredCultureCandidate(
  candidate: string | null | undefined,
  currentCulture: string | null | undefined,
  deviceCulture: string | null | undefined,
  allowlist: SupportedLocale[],
  defaultCulture: string,
): string {
  const tag =
    normalizeCultureTag(candidate) ||
    normalizeCultureTag(currentCulture) ||
    normalizeCultureTag(deviceCulture);
  return matchSupportedLocale(tag, allowlist, defaultCulture).culture;
}

/**
 * Resolve API / Accept-Language culture: saved profile → stored preference → token claim.
 */
export function resolveApiCultureCandidate(
  profileLanguage: string | null | undefined,
  preferredCulture: string | null | undefined,
  tokenLanguage: string | null | undefined,
  allowlist: SupportedLocale[],
  defaultCulture: string,
): string {
  const tag =
    normalizeCultureTag(profileLanguage) ||
    normalizeCultureTag(preferredCulture) ||
    normalizeCultureTag(tokenLanguage);
  return matchSupportedLocale(tag, allowlist, defaultCulture).culture;
}

/**
 * Build Accept-Language value: full culture, then language subtag with q=0.9.
 */
export function buildAcceptLanguageHeader(culture: string): string {
  const c = normalizeCultureTag(culture) || 'en-ZA';
  const lang = languageSubtag(c) || 'en';
  if (c.includes('-')) {
    return `${c},${lang};q=0.9`;
  }
  return c;
}
